var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import moment from 'moment';
import { Box, Chip, TableContainer, Tooltip } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import { openDialog } from 'store/commonReducer/common.action';
import { DIALOG_TYPES } from 'globals/global.constants';
import { taskManagementStyle } from '../TaskManagement.style';
import { ROLES_CONFIG } from 'config/role.config';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Table from 'common/Table/Table.component';
var TaskTable = function (props) {
    var dispatch = useAppDispatch();
    var classes = taskManagementStyle().classes;
    var loadingStatus = useAppSelector(function (state) { return state.taskManagement.loadingStatus; });
    var handleDeleteTask = React.useCallback(function (task) {
        var _a;
        (_a = props.onTaskDelete) === null || _a === void 0 ? void 0 : _a.call(props, task);
    }, [props.onTaskDelete]);
    var handleViewTask = React.useCallback(function (task) {
        dispatch(openDialog({
            type: DIALOG_TYPES.CREATE_TASK,
            dialogTitle: 'View Task',
            data: task,
            action: 'view',
        }));
    }, [dispatch]);
    var handleEditTask = React.useCallback(function (task) {
        dispatch(openDialog({
            type: DIALOG_TYPES.CREATE_TASK,
            dialogTitle: 'Edit Task',
            data: __assign(__assign({}, task), { onUpdate: props.onTaskUpdate }),
            action: 'edit',
        }));
    }, [dispatch, props.onTaskUpdate]);
    var headerData = React.useMemo(function () { return [
        {
            columnId: 'task_id',
            label: 'Task ID',
            width: 120,
        },
        {
            columnId: 'title',
            label: 'Title',
            width: 200,
        },
        {
            columnId: 'status',
            label: 'Status',
            width: 140,
        },
        {
            columnId: 'priority',
            label: 'Priority',
            width: 140,
        },
        {
            columnId: 'note_type',
            label: 'Incident/Note Type',
            width: 160,
        },
        {
            label: 'Created Date',
            columnId: 'created_date',
        },
        {
            columnId: 'due_date',
            label: 'Due Date',
            width: 180,
        },
        {
            columnId: 'ageing_days',
            label: 'Ageing (In Days)',
            width: 100,
        },
        {
            columnId: 'assigned_to_role',
            label: 'Role',
            width: 180,
        },
        {
            label: 'Assigned By',
            columnId: 'assigned_by_name',
        },
        {
            columnId: 'assigned_to_name',
            label: 'Assigned To',
            width: 180,
        },
        {
            columnId: 'member_name',
            label: 'Member Name',
            width: 140,
        },
        {
            columnId: 'action',
            label: '',
            width: 120,
        },
    ]; }, []);
    var rowData = React.useMemo(function () { return ({
        values: [
            {
                dataId: 'task_id',
            },
            {
                dataId: 'title',
            },
            {
                dataId: 'status',
                render: function (value) {
                    var config = {
                        open: {
                            label: 'OPEN',
                            bg: '#EEF4FF',
                            color: '#4F6EF7',
                        },
                        in_progress: {
                            label: 'IN PROGRESS',
                            bg: '#FFF7E6',
                            color: '#F59E0B',
                        },
                        completed: {
                            label: 'COMPLETED',
                            bg: '#E8FFF2',
                            color: '#10B981',
                        },
                        overdue: {
                            label: 'OVERDUE',
                            bg: '#FFECEC',
                            color: '#EF4444',
                        },
                        on_hold: {
                            label: 'ON HOLD',
                            bg: '#F3F4F6',
                            color: '#6B7280',
                        },
                        cancelled: {
                            label: 'CANCELLED',
                            bg: '#FFECEC',
                            color: '#EF4444',
                        },
                    };
                    var status = config[value === null || value === void 0 ? void 0 : value.toLowerCase()] || {
                        label: value,
                        bg: '#F3F4F6',
                        color: '#6B7280',
                    };
                    return (_jsx(Chip, { label: status.label, size: 'small', sx: {
                            backgroundColor: status.bg,
                            color: status.color,
                            fontWeight: 600,
                            fontSize: '11px',
                            height: 24,
                            borderRadius: '6px',
                            '& .MuiChip-label': {
                                px: 1.5,
                            },
                        } }));
                },
            },
            {
                dataId: 'priority',
                render: function (value) {
                    var config = {
                        high: {
                            label: 'HIGH',
                            bg: '#FFECEC',
                            color: '#EF4444',
                        },
                        medium: {
                            label: 'MEDIUM',
                            bg: '#FFF7E6',
                            color: '#F59E0B',
                        },
                        low: {
                            label: 'LOW',
                            bg: '#E8FFF2',
                            color: '#10B981',
                        },
                    };
                    var priority = config[value === null || value === void 0 ? void 0 : value.toLowerCase()] || {
                        label: value,
                        bg: '#F3F4F6',
                        color: '#6B7280',
                    };
                    return (_jsx(Chip, { label: priority.label, size: 'small', sx: {
                            backgroundColor: priority.bg,
                            color: priority.color,
                            fontWeight: 600,
                            fontSize: '11px',
                            height: 24,
                            borderRadius: '6px',
                            '& .MuiChip-label': {
                                px: 1.5,
                            },
                        } }));
                },
            },
            {
                dataId: 'note_type',
                format: function (value) { return (value ? value : '-'); },
            },
            {
                dataId: 'created_date',
                format: function (date) { return (date ? moment(date).format('MM/DD/YYYY') : '-'); },
            },
            {
                dataId: 'due_date',
                format: function (date) { return (date ? moment(date).format('MM/DD/YYYY') : '-'); },
            },
            {
                dataId: 'ageing_days',
                format: function (value) { return (value == null ? '-' : value); },
            },
            {
                dataId: 'assigned_to_role',
                format: function (assigned_to_role) { var _a; return (_a = ROLES_CONFIG[assigned_to_role]) === null || _a === void 0 ? void 0 : _a.accessLabel; },
            },
            {
                dataId: 'assigned_by_name',
                render: function (_, record) { return (_jsx(_Fragment, { children: record.assigned_by_name
                        ? "".concat(record.assigned_by_name).concat(record.assigned_by_employee_id
                            ? " (".concat(record.assigned_by_employee_id, ")")
                            : '')
                        : '-' })); },
            },
            {
                dataId: 'assigned_to_name',
                render: function (_, record) { return (_jsx(_Fragment, { children: "".concat(record.assigned_to_name, " (").concat(record.assigned_to_employee_id || '', ")") })); },
            },
            {
                dataId: 'member_name',
                render: function (_, record) { return (_jsx(_Fragment, { children: record.member_name
                        ? "".concat(record.member_name).concat(record.member_id ? " (".concat(record.member_id, ")") : '')
                        : '-' })); },
            },
            {
                dataId: '',
                render: function (_, record) { return (_jsxs(Box, { display: 'flex', gap: 1.5, alignItems: 'center', children: [_jsx(Tooltip, { title: 'View', children: _jsx(VisibilityOutlinedIcon, { sx: {
                                    color: '#1976D2',
                                    cursor: 'pointer',
                                    fontSize: 18,
                                }, onClick: function () { return handleViewTask(record); } }) }), record.status !== 'completed' && !props.disableEdit && (_jsx(Tooltip, { title: 'Edit', children: _jsx(EditOutlinedIcon, { sx: {
                                    color: '#16A34A',
                                    cursor: 'pointer',
                                    fontSize: 18,
                                }, onClick: function () { return handleEditTask(record); } }) })), record.status !== 'completed' &&
                            !record.task_id &&
                            record.localId && (_jsx(Tooltip, { title: 'Delete', children: _jsx(DeleteOutlineOutlinedIcon, { sx: {
                                    color: '#EF4444',
                                    cursor: 'pointer',
                                    fontSize: 18,
                                }, onClick: function () { return handleDeleteTask(record); } }) }))] })); },
            },
        ],
    }); }, [handleEditTask]);
    return (_jsx(Box, { className: classes.tableWrapper, children: _jsx(TableContainer, { children: _jsx(Table, { rowId: 'id', headerData: headerData, data: props.data, rowData: rowData, isDataLoading: loadingStatus, noDataMessage: 'No Tasks Found' }) }) }));
};
export default TaskTable;
